import React, { useRef, useState } from "react";
import { Pressable, View, Animated, Easing } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import CCText from "@/Atoms/CCText";
import { ClubSummary } from "@/Hooks/ClubsApi/types";
import { useMediaQuery } from "@/Hooks/useMediaQuery/useMediaQuery";
import ClubBadge from "./ClubBadge";
import { BADGES, DEFAULT_BADGE_BG } from "../badgeOptions";
import type { PieceKey } from "../clubPieces";

const BADGE_COLORS = ["#3DAB9E", "#E17846", "#67B3E1", "#937ADB", "#D1AB41"];
const badgeColorFor = (name: string) =>
  BADGE_COLORS[name.charCodeAt(0) % BADGE_COLORS.length];
const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find((b) => b.id === badgeId)?.piece ?? "pawn") as PieceKey;

// Directory (Browse) pill derived from visibility + join policy, matching the
// design's pillFor(): Open (green) / Approval (gold) / Private (purple) / Full.
function variantPill(club: ClubCardData): { label: string; bg: string; color: string } {
  if (club.full) return { label: `${club.member_count}/50 · Full`, bg: "#EDE6D2", color: "#A8A7A6" };
  if (club.visibility === "public" && club.join_policy === "open") {
    return { label: "Open", bg: "#E4F6EB", color: "#1E7A3D" };
  }
  if (club.visibility === "private") {
    return { label: "Private", bg: "#F3EEFF", color: "#6948C4" };
  }
  return { label: "Approval", bg: "#FAF0D1", color: "#806234" };
}

// Sub-line for Browse cards: "N members · <policy>".
function browseSub(club: ClubCardData): string {
  if (club.full) return `${club.member_count}/50 members · Full`;
  const policy =
    club.visibility === "private"
      ? "Private"
      : club.join_policy === "open"
      ? "Anyone can join"
      : "Approval needed";
  const members = club.official ? `${club.member_count}/50 members` : `${club.member_count} members`;
  return `${members} · ${policy}`;
}

// Some fields (rank / division / tier / unread / full) come from richer
// endpoints and may be absent on the summary; render them only when present.
type ClubCardData = ClubSummary & {
  rank?: number | null;
  division?: string | null;
  tier?: "gold" | "silver" | "bronze" | null;
  unread?: number | null;
  full?: boolean;
};

const tierColor = (tier?: string | null) =>
  ({ gold: "#D1AB41", silver: "#A8A29A", bronze: "#B5794A" } as const)[
    (tier ?? "gold") as "gold"
  ] ?? "#D1AB41";

function ClubStatusIcons({ club }: { club: ClubCardData }) {
  return (
    <>
      {club.official ? (
        <View
          testID={`club-card-official-${club.id}`}
          accessibilityLabel="Official club"
          style={{ width: 18, height: 18, alignItems: "center", justifyContent: "center" }}
        >
          <MaterialCommunityIcons name="check-decagram" size={18} color="#D2A51F" />
        </View>
      ) : null}
      {club.visibility === "private" ? (
        <View
          testID={`club-card-private-${club.id}`}
          accessibilityLabel="Private club"
          style={{
            width: 18,
            height: 18,
            borderRadius: 9,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F3EEFF",
          }}
        >
          <MaterialCommunityIcons name="lock" size={11} color="#6948C4" />
        </View>
      ) : null}
    </>
  );
}

export default function ClubCard({
  club,
  onPress,
  testID,
  variant = "browse",
  layout = "row",
}: {
  club: ClubCardData;
  onPress: () => void;
  testID?: string;
  variant?: "mine" | "browse";
  layout?: "row" | "grid";
}) {
  const { isMobileOrTab } = useMediaQuery();
  const isMine = variant === "mine";
  const isGrid = layout === "grid";
  const badgeSize = isGrid ? 56 : isMobileOrTab ? (isMine ? 52 : 46) : 56;
  const roleLabel = club.my_role
    ? club.my_role.charAt(0).toUpperCase() + club.my_role.slice(1)
    : null;
  const pill = variantPill(club);

  // Hover-lift animation (design: cc-hov → translateY(-3px) + shadow-pop).
  const lift = useRef(new Animated.Value(0)).current;
  const [hovered, setHovered] = useState(false);
  const animateTo = (to: number) =>
    Animated.timing(lift, {
      toValue: to,
      duration: 150,
      easing: Easing.out(Easing.ease),
      useNativeDriver: true,
    }).start();
  const translateY = lift.interpolate({ inputRange: [0, 1], outputRange: [0, -3] });

  const nameBlock = (
    <View style={{ flex: 1, minWidth: 0 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
        <CCText
          numberOfLines={1}
          style={{ flexShrink: 1, fontWeight: "600", color: "#262322", fontSize: 16 }}
        >
          {club.name}
        </CCText>
        <ClubStatusIcons club={club} />
      </View>
      <CCText style={{ color: "#888280", fontSize: 12, marginTop: 3 }}>
        {isMine ? `${club.member_count} members${roleLabel ? ` · ${roleLabel}` : ""}` : browseSub(club)}
      </CCText>
    </View>
  );

  const pillNode = (
    <View style={{ alignSelf: "flex-start", backgroundColor: pill.bg, borderRadius: 999, paddingVertical: 5, paddingHorizontal: 11 }}>
      <CCText style={{ fontSize: 12, fontWeight: "700", color: pill.color }}>{pill.label}</CCText>
    </View>
  );

  const crest =
    isMine && club.tier ? (
      <View style={{ padding: 3, borderRadius: 15, backgroundColor: tierColor(club.tier) }}>
        <ClubBadge
          piece={pieceFor(club.badge_id)}
          color={club.background_color || badgeColorFor(club.name) || DEFAULT_BADGE_BG}
          size={badgeSize - 6}
          radius={(badgeSize - 6) * 0.24}
        />
      </View>
    ) : (
      <ClubBadge
        piece={pieceFor(club.badge_id)}
        color={club.background_color || badgeColorFor(club.name) || DEFAULT_BADGE_BG}
        size={badgeSize}
        radius={badgeSize * 0.27}
      />
    );

  const cardShadow = hovered
    ? { shadowColor: "#4D3F37", shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.12, shadowRadius: 24, elevation: 6 }
    : { shadowColor: "#4D3F37", shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 8, elevation: 2 };

  // Grid (desktop Browse): stacked column card with pill bottom-left + hover lift.
  if (isGrid) {
    return (
      <Pressable
        onPress={onPress}
        testID={testID}
        onHoverIn={() => { setHovered(true); animateTo(1); }}
        onHoverOut={() => { setHovered(false); animateTo(0); }}
      >
        <Animated.View
          style={[
            {
              backgroundColor: "#fff",
              borderRadius: 16,
              padding: 20,
              borderWidth: 1,
              borderColor: "rgba(38,35,34,0.10)",
              gap: 12,
              transform: [{ translateY }],
            },
            cardShadow,
          ]}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 13 }}>
            {crest}
            {nameBlock}
          </View>
          {pillNode}
        </Animated.View>
      </Pressable>
    );
  }

  // Row layout (mobile list, My-Clubs).
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      onHoverIn={() => { setHovered(true); animateTo(1); }}
      onHoverOut={() => { setHovered(false); animateTo(0); }}
    >
      <Animated.View
        style={[
          {
            padding: isMine ? 14 : 13,
            backgroundColor: "#fff",
            borderRadius: isMine ? 16 : 14,
            flexDirection: "row",
            alignItems: "center",
            gap: isMine ? 14 : 12,
            position: "relative",
            transform: [{ translateY }],
          },
          cardShadow,
          isMine ? null : { borderWidth: 1, borderColor: "rgba(38,35,34,0.10)" },
        ]}
      >
        {crest}
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
            <CCText
              numberOfLines={1}
              style={{ flexShrink: 1, fontWeight: "600", color: "#262322", fontSize: isMine ? 16 : 15 }}
            >
              {club.name}
            </CCText>
            <ClubStatusIcons club={club} />
          </View>
          <CCText style={{ color: "#888280", fontSize: isMine ? 12.5 : 12, marginTop: isMine ? 2 : 3 }}>
            {isMine ? `${club.member_count} members${roleLabel ? ` · ${roleLabel}` : ""}` : browseSub(club)}
          </CCText>

          {isMine && (club.rank != null || club.division) ? (
            <View style={{ marginTop: 6, flexDirection: "row", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {club.rank != null ? (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 5, backgroundColor: "#FAF0D1", borderRadius: 999, paddingVertical: 3, paddingHorizontal: 9 }}>
                  <CCText style={{ fontSize: 11.5, fontWeight: "600", color: "#806234" }}>🏆 Rank #{club.rank}</CCText>
                </View>
              ) : null}
              {club.division ? (
                <CCText style={{ fontSize: 11, fontWeight: "600", color: tierColor(club.tier) }}>♛ {club.division}</CCText>
              ) : null}
            </View>
          ) : null}
        </View>

        {!isMine ? (
          <View style={{ backgroundColor: pill.bg, borderRadius: 999, paddingVertical: 5, paddingHorizontal: 10 }}>
            <CCText style={{ fontSize: 11, fontWeight: "700", color: pill.color }}>{pill.label}</CCText>
          </View>
        ) : null}

        {isMine && club.unread ? (
          <View style={{ position: "absolute", top: 14, right: 14, minWidth: 20, height: 20, paddingHorizontal: 6, borderRadius: 999, backgroundColor: "#E17B21", alignItems: "center", justifyContent: "center" }}>
            <CCText style={{ color: "#fff", fontSize: 11, fontWeight: "700" }}>{club.unread}</CCText>
          </View>
        ) : null}
      </Animated.View>
    </Pressable>
  );
}
